/**
 * Simulation Run Detail Page
 *
 * Shows run header, trace nodes, evaluation results, and telemetry for a single run.
 * Route: /simulation/runs/:id
 */
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import {
  BeakerIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  ArrowsRightLeftIcon,
  ArrowUturnLeftIcon,
  CpuChipIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { simulationStore } from '@/stores/SimulationStore'
import type { TraceNode, CriterionResult } from '@/stores/SimulationStore'

type TabId = 'trace' | 'evaluation' | 'telemetry'

function formatDuration(ms?: number | null) {
  if (!ms) return 'N/A'
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

function getStatusBadgeVariant(status: string): 'primary' | 'success' | 'warning' | 'error' | 'gray' {
  const variants: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
    pending: 'gray',
    running: 'primary',
    passed: 'success',
    failed: 'error',
    error: 'warning',
    completed: 'success',
    skipped: 'gray',
  }
  return variants[status] || 'gray'
}

function TraceNodeRow({ node, isExpanded, onToggle }: {
  node: TraceNode
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="border border-border rounded-lg">
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50"
        onClick={onToggle}
      >
        {isExpanded ? (
          <ChevronDownIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}

        <span className="font-medium text-foreground flex-1">{node.name}</span>

        <Badge variant={getStatusBadgeVariant(node.status)} size="sm">
          {node.status}
        </Badge>

        <span className="text-sm text-muted-foreground w-20 text-right">
          {formatDuration(node.duration_ms)}
        </span>

        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1" title="LLM calls">
            <CpuChipIcon className="w-4 h-4" /> {node.llm_calls}
          </span>
          <span className="flex items-center gap-1" title="Tool calls">
            <WrenchScrewdriverIcon className="w-4 h-4" /> {node.tool_calls}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border space-y-3 mt-0 pt-3">
          {node.input && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Input</h4>
              <pre className="bg-muted/50 border border-border rounded p-3 text-xs overflow-x-auto max-h-48">
                {JSON.stringify(node.input, null, 2)}
              </pre>
            </div>
          )}
          {node.output && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Output</h4>
              <pre className="bg-muted/50 border border-border rounded p-3 text-xs overflow-x-auto max-h-48">
                {JSON.stringify(node.output, null, 2)}
              </pre>
            </div>
          )}
          {node.llm_details && node.llm_details.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">LLM Calls</h4>
              <pre className="bg-blue-500/10 border border-blue-500/20 rounded p-3 text-xs overflow-x-auto max-h-48">
                {JSON.stringify(node.llm_details, null, 2)}
              </pre>
            </div>
          )}
          {node.tool_details && node.tool_details.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Tool Calls</h4>
              <pre className="bg-green-500/10 border border-green-500/20 rounded p-3 text-xs overflow-x-auto max-h-48">
                {JSON.stringify(node.tool_details, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const SimulationRunPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabId>('trace')
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set())
  const [compareRunId, setCompareRunId] = useState('')

  useEffect(() => {
    if (!id) return
    simulationStore.fetchRunById(id)
    simulationStore.fetchTrace(id)
    simulationStore.fetchEvaluation(id)

    return () => {
      simulationStore.clearSelectedRun()
    }
  }, [id])

  const run = simulationStore.selectedRun
  const trace = simulationStore.currentTrace
  const evaluation = simulationStore.currentEvaluation

  const toggleNode = (index: number) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  const handleCompare = () => {
    if (compareRunId.trim() && id) {
      simulationStore.compareTwoRuns(id, compareRunId.trim())
    }
  }

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'trace', label: 'Trace' },
    { id: 'evaluation', label: 'Evaluation' },
    { id: 'telemetry', label: 'Telemetry' },
  ]

  if (simulationStore.runsLoading && !run) {
    return (
      <div className="space-y-6">
        <Card className="animate-pulse">
          <div className="h-24 bg-muted rounded" />
        </Card>
        <Card className="animate-pulse">
          <div className="h-64 bg-muted rounded" />
        </Card>
      </div>
    )
  }

  if (!run) {
    return (
      <Card className="text-center py-12">
        <XCircleIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Run not found</h3>
        <Button variant="secondary" onClick={() => navigate('/simulation')}>
          Back to Dashboard
        </Button>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/simulation')}>
        <ArrowLeftIcon className="w-4 h-4 mr-1" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-500/15 rounded-lg">
              <BeakerIcon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{run.agent_name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <Badge variant={getStatusBadgeVariant(run.status)}>
                  {run.status}
                </Badge>
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <ClockIcon className="w-4 h-4" />
                  {formatDuration(run.duration_ms)}
                </span>
                {run.score != null && (
                  <span className="text-sm font-medium text-foreground">
                    Score: {run.score.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/simulation/runs/${id}/replay`)}
            >
              <ArrowPathIcon className="w-4 h-4 mr-1" />
              Replay
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const inputId = window.prompt('Enter Run ID to compare with:')
                if (inputId) {
                  setCompareRunId(inputId)
                  handleCompare()
                }
              }}
            >
              <ArrowsRightLeftIcon className="w-4 h-4 mr-1" />
              Compare with...
            </Button>
            <Button variant="ghost" size="sm" title="Branch from this run (not yet implemented)">
              <ArrowUturnLeftIcon className="w-4 h-4 mr-1" />
              Branch from...
            </Button>
          </div>
        </div>

        {run.error_message && (
          <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <p className="text-sm text-red-600">
              <span className="font-semibold">Error: </span>
              {run.error_message}
            </p>
          </div>
        )}
      </Card>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-6" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'trace' && (
        <div className="space-y-2">
          {simulationStore.traceLoading ? (
            <Card className="animate-pulse">
              <div className="h-48 bg-muted rounded" />
            </Card>
          ) : trace && trace.nodes.length > 0 ? (
            trace.nodes.map((node, idx) => (
              <TraceNodeRow
                key={idx}
                node={node}
                isExpanded={expandedNodes.has(idx)}
                onToggle={() => toggleNode(idx)}
              />
            ))
          ) : (
            <Card className="text-center py-8 text-muted-foreground">
              No trace data available for this run.
            </Card>
          )}

          {trace && (
            <div className="text-sm text-muted-foreground text-right">
              Total duration: {formatDuration(trace.total_duration_ms)}
            </div>
          )}
        </div>
      )}

      {activeTab === 'evaluation' && (
        <Card>
          {simulationStore.evaluationLoading ? (
            <div className="animate-pulse">
              <div className="h-48 bg-muted rounded" />
            </div>
          ) : evaluation ? (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-lg font-semibold text-foreground">Evaluation Results</h3>
                <Badge variant={evaluation.overall_passed ? 'success' : 'error'}>
                  {evaluation.overall_passed ? 'PASSED' : 'FAILED'}
                </Badge>
              </div>

              {evaluation.criteria_results.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border">
                        <th className="pb-2 font-medium">Criterion</th>
                        <th className="pb-2 font-medium">Result</th>
                        <th className="pb-2 font-medium">Detail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {evaluation.criteria_results.map((result: CriterionResult, idx: number) => (
                        <tr key={idx} className="hover:bg-muted/50">
                          <td className="py-3 font-medium text-foreground">{result.criterion}</td>
                          <td className="py-3">
                            {result.passed ? (
                              <Badge variant="success" size="sm">
                                <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                                Passed
                              </Badge>
                            ) : (
                              <Badge variant="error" size="sm">
                                <XCircleIcon className="w-3.5 h-3.5 mr-1" />
                                Failed
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 text-muted-foreground">{result.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground">No criteria results available.</p>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No evaluation data available for this run.
            </div>
          )}
        </Card>
      )}

      {activeTab === 'telemetry' && (
        <div>
          {trace ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-500/15 rounded-lg">
                    <CpuChipIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-foreground">Tokens</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total LLM Calls</span>
                    <span className="font-medium">
                      {trace.nodes.reduce((sum, n) => sum + n.llm_calls, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Tool Calls</span>
                    <span className="font-medium">
                      {trace.nodes.reduce((sum, n) => sum + n.tool_calls, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Nodes Executed</span>
                    <span className="font-medium">{trace.nodes.length}</span>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-500/15 rounded-lg">
                    <CurrencyDollarIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-foreground">Cost</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Estimated Cost</span>
                    <span className="font-medium">
                      ${(trace.nodes.reduce((sum, n) => sum + n.llm_calls, 0) * 0.003).toFixed(4)}
                    </span>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-500/15 rounded-lg">
                    <ClockIcon className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-foreground">Latency Breakdown</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Duration</span>
                    <span className="font-medium">{formatDuration(trace.total_duration_ms)}</span>
                  </div>
                  {trace.nodes.map((node, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-muted-foreground truncate max-w-[60%]">{node.name}</span>
                      <span className="text-foreground">{formatDuration(node.duration_ms)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          ) : (
            <Card className="text-center py-8 text-muted-foreground">
              No telemetry data available for this run.
            </Card>
          )}
        </div>
      )}

      {/* Compare Results */}
      {simulationStore.compareResult && (
        <Card>
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Comparison: {simulationStore.compareResult.run_a.id.slice(0, 8)} vs{' '}
            {simulationStore.compareResult.run_b.id.slice(0, 8)}
          </h3>
          <pre className="bg-muted/50 border border-border rounded-lg p-4 text-sm overflow-x-auto max-h-96">
            {JSON.stringify(simulationStore.compareResult, null, 2)}
          </pre>
        </Card>
      )}
    </div>
  )
})

export default SimulationRunPage
